/* HCE cigar Presentation ordering rule — 2026-09-12
   Applies to every product picker whose option name is exactly "Presentation".
   Primary rule: smallest quantity first. Secondary rule: presentation type.
   This keeps the storefront consistent even if Shopify/Admin variant values were created in a different order. */

(() => {
  if (window.HCEPresentationOrder) return;

  const keyFor = (label) => {
    const text = String(label || '').trim();
    const normal = text
      .toLowerCase()
      .replace(/[–—]/g, '-')
      .replace(/\s+/g, ' ');

    let quantity = Number.POSITIVE_INFINITY;
    let typeRank = 900;

    /* All single-cigar formats are quantity 1. */
    if (/^single\b/.test(normal)) {
      quantity = 1;

      if (/^single\s+cigar\b/.test(normal) && !/\bcoffin\b/.test(normal) && !/\btub(?:o|os)\b/.test(normal)) {
        typeRank = 0;
      } else if (/^single\s+tub(?:o|os)\b/.test(normal) || /^single\b.*\btub(?:o|os)\b/.test(normal)) {
        typeRank = 1;
      } else if (/^single\s+coffin\b/.test(normal) || /^single\b.*\bcoffin\b/.test(normal)) {
        typeRank = 2;
      } else {
        typeRank = 3;
      }
    } else {
      const numberMatch = normal.match(/\b(\d{1,3})\b/);
      if (numberMatch) quantity = parseInt(numberMatch[1], 10);

      /* For equal quantities, keep the familiar HCE hierarchy. */
      if (/^pack\b/.test(normal)) {
        typeRank = /tub(?:o|os)\b/.test(normal) ? 11 : 10;
      } else if (/^box\b/.test(normal)) {
        typeRank = /tub(?:o|os)\b/.test(normal) ? 21 : 20;
      } else if (/^cabinet\b/.test(normal)) {
        typeRank = 30;
      } else if (/^jar\b/.test(normal)) {
        typeRank = 40;
      } else if (/^(?:travel\s+)?humidor\b/.test(normal)) {
        typeRank = 50;
      } else if (/^book\b/.test(normal)) {
        typeRank = 60;
      } else if (/^tin\b/.test(normal)) {
        typeRank = 70;
      } else if (/^gift\s+box\b/.test(normal)) {
        typeRank = 80;
      } else if (/^bundle\b/.test(normal)) {
        typeRank = 90;
      } else if (/^outer\s+pack\b/.test(normal)) {
        typeRank = 100;
      } else if (/\bpack\b/.test(normal)) {
        typeRank = 110;
      } else if (/\bbox\b/.test(normal)) {
        typeRank = 120;
      } else if (/\bjar\b/.test(normal)) {
        typeRank = 130;
      } else if (/\bhumidor\b/.test(normal)) {
        typeRank = 140;
      }
    }

    return { quantity, typeRank, text: normal };
  };

  const comparePresentation = (a, b) => {
    const left = keyFor(a);
    const right = keyFor(b);

    if (left.quantity !== right.quantity) return left.quantity - right.quantity;
    if (left.typeRank !== right.typeRank) return left.typeRank - right.typeRank;
    return left.text.localeCompare(right.text);
  };

  const getLabelForInput = (wrapper, input) => {
    return Array.from(wrapper.querySelectorAll('label')).find((label) => label.htmlFor === input.id) || null;
  };

  const sortRadioWrapper = (wrapper) => {
    const inputs = Array.from(wrapper.querySelectorAll('input[type="radio"]')).filter(
      (input) => input.name && input.name.startsWith('Presentation-')
    );

    if (inputs.length < 2) return;

    const groups = inputs.map((input) => {
      const value = input.value;
      const groupedParent = input.closest('.option-thumbnail-picker-wrapper, .option-color-picker-wrapper');

      if (groupedParent && groupedParent.parentElement === wrapper) {
        return { value, nodes: [groupedParent] };
      }

      const label = getLabelForInput(wrapper, input);
      return { value, nodes: label ? [input, label] : [input] };
    });

    const current = groups.map((group) => group.value);
    const sorted = groups.slice().sort((a, b) => comparePresentation(a.value, b.value));
    const desired = sorted.map((group) => group.value);

    if (current.every((value, index) => value === desired[index])) return;

    const fragment = document.createDocumentFragment();
    sorted.forEach((group) => group.nodes.forEach((node) => fragment.appendChild(node)));
    wrapper.appendChild(fragment);
  };

  const sortDropdown = (select) => {
    if (select.name !== 'options[Presentation]') return;

    const options = Array.from(select.options);
    if (options.length < 2) return;

    const sorted = options.slice().sort((a, b) => comparePresentation(a.value, b.value));
    const unchanged = options.every((option, index) => option === sorted[index]);
    if (unchanged) return;

    const selectedValue = select.value;
    sorted.forEach((option) => select.appendChild(option));
    select.value = selectedValue;
  };

  const sortAll = (root = document) => {
    const scope = root.querySelectorAll ? root : document;

    scope.querySelectorAll('.radio-options-wrapper').forEach(sortRadioWrapper);
    scope.querySelectorAll('select[name="options[Presentation]"]').forEach(sortDropdown);

    if (root.matches?.('.radio-options-wrapper')) sortRadioWrapper(root);
    if (root.matches?.('select[name="options[Presentation]"]')) sortDropdown(root);
  };

  window.HCEPresentationOrder = {
    sort: sortAll,
    compare: comparePresentation,
    keyFor
  };

  /* Defer scripts run after the product markup is parsed, so sort before the first useful paint. */
  sortAll();

  /* Mayfair replaces variant-selects HTML when a customer changes an option.
     Re-apply the HCE order to the fresh markup automatically. */
  const observer = new MutationObserver((records) => {
    let needsSort = false;

    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node.nodeType !== 1) continue;

        if (
          node.matches?.('variant-selects, .radio-options-wrapper, select[name="options[Presentation]"]') ||
          node.querySelector?.('variant-selects, .radio-options-wrapper, select[name="options[Presentation]"]')
        ) {
          needsSort = true;
          break;
        }
      }
      if (needsSort) break;
    }

    if (needsSort) sortAll();
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('product-info:loaded', () => sortAll());
})();
